import { Injectable } from '@angular/core';
import { RequestOptions, Response, ResponseContentType, URLSearchParams } from '@angular/http';
import { Observable } from 'rxjs/Observable';

import { environment } from '../../../../environments/environment';
import { CheckBuilder } from '../../models/builders/check.builder';
import { Check } from '../../models/check';
import { getCurrentMillis, getMillisFromDateLikeString, getMomentFromDayMonthAndYear } from '../../utils/date.utils';
import { HttpService } from '../base/http.service';

const checkRequest = (check: Check) => {
  return {
    start: check.startTime,
    end: check.endTime,
    hash: check.user
  };
};

const extractFileData = (response: Response) => {
  return {
    file: response.blob(),
    type: response.headers.get('Content-Type')
  };
};

@Injectable()
export class CheckHttpService {
  private apiUrl: string;

  constructor(private httpService: HttpService) {
    this.apiUrl = `${environment.apiUrl}checks`;
  }

  createCheck(check: Check) {
    const postBody = checkRequest(check);
    return this.httpService.post(`${this.apiUrl}/register-check`, postBody);
  }

  deleteCheck(hash: string) {
    return this.httpService.delete(`${this.apiUrl}/${hash}`);
  }

  doCheck(closeIntent: boolean = false): Observable<string> {
    const postBody = { closeIntent };
    return this.httpService.post(`${this.apiUrl}`, postBody)
      .map((response: Response) => response.json())
      .map((json: any) => json.hash);
  }

  downloadReports(month: number, year: number, employees: string[]) {
    const params = new URLSearchParams();
    params.append('employees', (employees) ? employees.join(',') : undefined);
    params.append('month', month.toString());
    params.append('year', year.toString());
    const options = new RequestOptions();
    options.params = params;
    options.responseType = ResponseContentType.ArrayBuffer;

    return this.httpService.get(`${this.apiUrl}/download`, options)
      .map(extractFileData);
  }

  getChecks(start: number, end: number, page: number, count: number, hash: string = null) {
    const params = new URLSearchParams();
    const from = ((page > 0) ? page - 1 : 0) * count;
    params.append('employee', hash);
    params.append('start', start.toString());
    params.append('end', end.toString());
    params.append('from', from.toString());
    params.append('count', count.toString());
    const options = new RequestOptions();
    options.params = params;

    return this.httpService.get(`${this.apiUrl}/employee/grouped`, options)
      .map((response: Response) => response.json())
      .map(
        (data) => {
          data.checks = CheckBuilder.groupedChecksFromJsonList(data.checks);
          return data;
        }
      );
  }

  getUserChecks(start: number, end: number, page: number, count: number) {
    const params = new URLSearchParams();
    const from = ((page > 0) ? page - 1 : 0) * count;
    params.append('start', start.toString());
    params.append('end', end.toString());
    params.append('from', from.toString());
    params.append('count', count.toString());
    const options = new RequestOptions();
    options.params = params;

    return this.httpService.get(`${this.apiUrl}/me/grouped`, options)
      .map((response: Response) => response.json())
      .map(
        (data) => {
          data.checks = CheckBuilder.groupedChecksFromJsonList(data.checks);
          return data;
        }
      );
  }

  getChecksByMonthAndYear(month: number, year: number) {
    const date = getMomentFromDayMonthAndYear(1, month, year);
    const params = new URLSearchParams();
    params.append('start', date.startOf('month').valueOf().toString());
    params.append('end', date.endOf('month').valueOf().toString());
    const options = new RequestOptions();
    options.params = params;

    return this.httpService.get(`${this.apiUrl}/grouped`, options)
      .map((response: Response) => response.json())
      .map(CheckBuilder.groupedChecksFromJsonList);
  }

  getChecksOnDate(hash: string, date: number) {
    const params = new URLSearchParams();
    params.append('date', date.toString());
    const options = new RequestOptions();
    options.params = params;

    return this.httpService.get(`${this.apiUrl}/employee/${hash}/date`, options)
      .map((response: Response) => response.json())
      .map(CheckBuilder.checksFromJsonList);
  }

  getUserChecksOnDate(date: number) {
    const params = new URLSearchParams();
    params.append('date', date.toString());
    const options = new RequestOptions();
    options.params = params;

    return this.httpService.get(`${this.apiUrl}/user/date`, options)
      .map((response: Response) => response.json())
      .map(CheckBuilder.checksFromJsonList);
  }

  getClosedChecks(start: number, end: number) {
    const params = new URLSearchParams();
    params.append('start', start.toString());
    params.append('end', end.toString());
    const options = new RequestOptions();
    options.params = params;

    return this.httpService.get(`${this.apiUrl}/closed`, options)
      .map((response: Response) => response.json())
      .map(CheckBuilder.checksFromJsonList);
  }

  getOpenCheck() {
    return this.httpService.get(`${this.apiUrl}/open`)
      .map((response: Response) => {
        const check = CheckBuilder.checkFromJson(response.json());
        check.serverDiff = getCurrentMillis() - getMillisFromDateLikeString(response.headers.get('date'));
        return check;
      });
  }

  getRealTimeChecks() {
    return this.httpService.get(`${this.apiUrl}/company/open`)
      .map((response: Response) => response.json())
      .map(CheckBuilder.realTimeChecksFromJsonList);
  }

  requestEditCheck(check: Check) {
    const postBody = { start: check.startTime, end: check.endTime };
    return this.httpService.post(`${this.apiUrl}/requests/${check.hash}`, postBody);
  }

  requestNewCheck(check: Check) {
    const postBody = { start: check.startTime, end: check.endTime };
    return this.httpService.post(`${this.apiUrl}/requests`, postBody);
  }

  saveObservations(hash: string, observations: string) {
    const putBody = { observations };
    return this.httpService.put(`${this.apiUrl}/${hash}/observations`, putBody);
  }

  updateCheck(check: Check) {
    const putBody = checkRequest(check);
    return this.httpService.put(`${this.apiUrl}/register-check/${check.hash}`, putBody);
  }
}



// WEBPACK FOOTER //
// /var/www/lantago-app/lantago-app-demo/src/app/core/services/api/check-http.service.ts